import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, X, Calendar } from 'lucide-react';
import { eventosAPI } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';

const tipos = ['treino', 'amistoso', 'torneio', 'campeonato'];

export default function AdminEventos() {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvento, setEditingEvento] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    data: '',
    local: '',
    tipo: 'treino'
  });
  const { toast } = useToast();

  const fetchEventos = async () => {
    try {
      const res = await eventosAPI.listar();
      setEventos(res.data);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEventos();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvento) {
        await eventosAPI.atualizar(editingEvento.id, formData);
        toast({ title: "Evento atualizado com sucesso!" });
      } else {
        await eventosAPI.criar(formData);
        toast({ title: "Evento criado com sucesso!" });
      }
      
      setModalOpen(false);
      setEditingEvento(null);
      setFormData({ titulo: '', descricao: '', data: '', local: '', tipo: 'treino' });
      fetchEventos();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast({ title: "Erro ao salvar", variant: "destructive" });
    }
  };

  const handleEdit = (evento) => {
    setEditingEvento(evento);
    setFormData({
      titulo: evento.titulo,
      descricao: evento.descricao || '',
      data: evento.data,
      local: evento.local,
      tipo: evento.tipo
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este evento?')) return;
    try {
      await eventosAPI.deletar(id);
      toast({ title: "Evento excluído com sucesso!" });
      fetchEventos();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getTipoBadge = (tipo) => {
    const badges = {
      treino: 'bg-green-100 text-green-700',
      amistoso: 'bg-blue-100 text-blue-700',
      torneio: 'bg-yellow-100 text-yellow-700',
      campeonato: 'bg-purple-100 text-purple-700'
    };
    return badges[tipo] || badges.treino;
  };

  return (
    <div data-testid="admin-eventos">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Eventos</h1>
          <p className="text-slate-600 mt-1">Gerencie os eventos e competições</p>
        </div>
        <button
          onClick={() => {
            setEditingEvento(null);
            setFormData({ titulo: '', descricao: '', data: '', local: '', tipo: 'treino' });
            setModalOpen(true);
          }}
          data-testid="add-evento-btn"
          className="bg-blue-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 hover:bg-blue-800 transition-colors"
        >
          <Plus size={20} />
          Adicionar Evento
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : eventos.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center">
          <Calendar className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-xl font-bold text-slate-700 mb-2">Nenhum evento cadastrado</h3>
          <p className="text-slate-500">Clique em "Adicionar Evento" para começar.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Evento</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Data</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Local</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Tipo</th>
                <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {eventos.map((evento) => (
                <tr key={evento.id} className="hover:bg-slate-50" data-testid={`evento-row-${evento.id}`}>
                  <td className="px-6 py-4">
                    <span className="font-medium text-slate-900">{evento.titulo}</span>
                    {evento.descricao && (
                      <p className="text-sm text-slate-500 mt-1 truncate max-w-xs">{evento.descricao}</p>
                    )}
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {new Date(evento.data).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-6 py-4 text-slate-600">{evento.local}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${getTipoBadge(evento.tipo)}`}>
                      {evento.tipo}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleEdit(evento)}
                      data-testid={`edit-evento-${evento.id}`}
                      className="text-blue-600 hover:text-blue-800 p-2"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(evento.id)}
                      data-testid={`delete-evento-${evento.id}`}
                      className="text-red-600 hover:text-red-800 p-2"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="evento-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl w-full max-w-lg"
          >
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="font-heading text-xl font-bold">
                {editingEvento ? 'Editar Evento' : 'Novo Evento'}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Título *</label>
                <input
                  type="text"
                  name="titulo"
                  value={formData.titulo}
                  onChange={handleChange}
                  required
                  data-testid="modal-input-titulo"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  placeholder="Nome do evento"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Data *</label>
                  <input
                    type="date"
                    name="data"
                    value={formData.data}
                    onChange={handleChange}
                    required
                    data-testid="modal-input-data"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Tipo *</label>
                  <select
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleChange}
                    required
                    data-testid="modal-select-tipo"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  >
                    {tipos.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Local *</label>
                <input
                  type="text"
                  name="local"
                  value={formData.local}
                  onChange={handleChange}
                  required
                  data-testid="modal-input-local"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100"
                  placeholder="Local do evento"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Descrição</label>
                <textarea
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleChange}
                  rows={3}
                  data-testid="modal-input-descricao"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-blue-700 focus:ring-2 focus:ring-blue-100 resize-none"
                  placeholder="Detalhes do evento..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="flex-1 px-6 py-3 border border-slate-300 rounded-lg font-bold text-slate-700 hover:bg-slate-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="modal-submit-btn"
                  className="flex-1 px-6 py-3 bg-blue-700 text-white rounded-lg font-bold hover:bg-blue-800"
                >
                  {editingEvento ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
